import { PlayTabs } from './Train';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RECOGNIZERS, WALK_CHORDS, WALK_KEYS } from '../../data/walk';
import { pathById } from '../../data';
import { preludeFiguration } from '../../music/figuration';
import { keyName, parseKey } from '../../music/key';
import { ChordToken, findFifthsSequences, matchPatterns } from '../../music/matcher';
import { mod, pcNumber, pitchName } from '../../music/pitch';
import { Chord, formatRoman, identifyChord, realizeRoman } from '../../music/roman';
import { standaloneTension } from '../../music/tension';
import { spellMidi, voiceChord } from '../../music/voicing';
import { usePlayer, usePrefs } from '../hooks';
import { layoutTerrain, toPlayNotes } from '../model';
import { Well } from '../components/Well';
import type { PlayNote } from '../../audio/player';

type Texture = 'block' | 'prelude' | 'alberti';
type Inversion = 'root' | 'first' | 'second' | 'smooth';

interface Step {
  base: string;
  symbol: string;
  chord: Chord;
  midi: number[];
  tension: number;
}

function invertSymbol(symbol: string, inv: 0 | 1 | 2 | 3): string {
  if (symbol === 'Cad64' || symbol === 'N6') return symbol;
  const [main, applied] = symbol.split('/');
  const m = /^(.*?)(7)?$/.exec(main)!;
  const seventh = !!m[2];
  const body = m[1];
  const fig = seventh ? ['7', '65', '43', '42'][inv] : ['', '6', '64', '64'][inv];
  return body + fig + (applied ? `/${applied}` : '');
}

/** Notes for one chord in the chosen texture, starting at `t` (quarters). */
function textureNotes(midi: number[], t: number, texture: Texture, chord: Chord): PlayNote[] {
  if (texture === 'block') return midi.map((m, i) => ({ onset: t, dur: 2, midi: m, line: i, velocity: i === 0 ? 0.8 : 0.66 }));
  if (texture === 'alberti') {
    const [b, lo, mid, hi] = midi;
    const order = [lo, hi, mid, hi, lo, hi, mid, hi];
    return [{ onset: t, dur: 2, midi: b, line: 0, velocity: 0.78 }, ...order.map((m, i) => ({ onset: t + i * 0.25, dur: 0.25, midi: m, line: 1, velocity: i % 2 === 0 ? 0.62 : 0.55 }))];
  }
  // Bach's prelude figuration: add one chord tone above the top voice to make five.
  let extra = midi[midi.length - 1] + 1;
  while (!chord.pcs.includes(mod(extra, 12))) extra++;
  const five = [...midi, extra].map((m) => pitchName(spellMidi(m, chord.members)));
  return toPlayNotes(preludeFiguration(five, t, 0));
}

export function Walk() {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player } = usePlayer();
  const [keyToken, setKeyToken] = useState('C');
  const [texture, setTexture] = useState<Texture>('block');
  const [inversion, setInversion] = useState<Inversion>('root');
  const [steps, setSteps] = useState<Step[]>([]);
  const [midiStatus, setMidiStatus] = useState<'off' | 'on' | 'unavailable'>('off');
  const key = useMemo(() => parseKey(keyToken), [keyToken]);
  const nodes = useMemo(() => layoutTerrain(WALK_CHORDS, key), [key]);

  useEffect(() => player.setTempo(prefs.tempo), [player, prefs.tempo]);

  const add = (baseSymbol: string, played?: number[]) => {
    const prev = steps[steps.length - 1];
    let symbol = baseSymbol;
    const base = realizeRoman(baseSymbol, key);
    const special = baseSymbol === 'Cad64' || baseSymbol === 'N6';
    if (played && !special) {
      // Your own voicing: read the inversion from the lowest note you played.
      const inv = base.members.findIndex((m) => pcNumber(m) === mod(played[0], 12));
      symbol = invertSymbol(baseSymbol, Math.max(0, inv) as 0 | 1 | 2 | 3);
    } else if (inversion !== 'smooth' && !special) {
      symbol = invertSymbol(baseSymbol, inversion === 'root' ? 0 : inversion === 'first' ? 1 : 2);
    } else if (prev && !special && !(prev.chord.fn === 'D' && base.fn === 'T')) {
      // Auto bass: prefer root position, but take an inversion when it makes the bass move by step
      // or hold a common tone. Dominants keep their root more readily, as in cadences.
      let bestDist = Infinity;
      for (let inv = 0; inv < (base.seventh ? 4 : 2); inv++) {
        const s = invertSymbol(baseSymbol, inv as 0 | 1 | 2 | 3);
        const diff = mod(pcNumber(realizeRoman(s, key).bass) - mod(prev.midi[0], 12), 12);
        const leap = Math.min(diff, 12 - diff);
        const d = inv === 0 ? leap * 0.5 : leap + 1.5 + (base.fn === 'D' && !base.applied ? 2 : 0);
        if (d < bestDist) {
          symbol = s;
          bestDist = d;
        }
      }
    }
    const chord = realizeRoman(symbol, key);
    const midi = played ?? voiceChord(chord, { prev: prev?.midi ?? null, prevChord: prev?.chord ?? null });
    const tension = standaloneTension(chord, midi, key);
    const next = [...steps, { base: baseSymbol, symbol, chord, midi, tension }].slice(-24);
    setSteps(next);
    player.setNotes(textureNotes(midi, 0, texture, chord), 2);
    void player.play(0);
  };

  const replay = () => {
    const notes = steps.flatMap((s, i) => textureNotes(s.midi, i * 2, texture, s.chord));
    player.setNotes(notes, steps.length * 2);
    void player.play(0);
  };

  const tokens: ChordToken[] = useMemo(() => steps.map((s) => ({ symbol: s.symbol, chord: s.chord, key })), [steps, key]);
  const found = useMemo(() => {
    const all = [...matchPatterns(tokens, RECOGNIZERS), ...findFifthsSequences(tokens)];
    const last = tokens.length - 1;
    return all
      .filter((m) => m.end === last)
      .sort((a, b) => b.end - b.start - (a.end - a.start))
      .filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i)
      .slice(0, 3);
  }, [tokens]);

  // Tonicization: an applied chord followed by its target.
  const lastTwo = steps.slice(-2);
  const tonicized =
    lastTwo.length === 2 && lastTwo[0].chord.applied && pcNumber(lastTwo[1].chord.root) === pcNumber(lastTwo[0].chord.localKey.tonic);

  // Web MIDI: hold a chord on your keyboard to place it on the terrain.
  const held = useRef(new Set<number>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addRef = useRef(add);
  addRef.current = add;
  const connectMidi = async () => {
    const nav = navigator as Navigator & { requestMIDIAccess?: () => Promise<MIDIAccess> };
    if (!nav.requestMIDIAccess) return setMidiStatus('unavailable');
    try {
      const access = await nav.requestMIDIAccess();
      access.inputs.forEach((input) => {
        input.onmidimessage = (e: MIDIMessageEvent) => {
          const [status, note, vel] = e.data ?? [];
          const cmd = (status ?? 0) & 0xf0;
          if (cmd === 0x90 && vel > 0) held.current.add(note);
          else if (cmd === 0x80 || (cmd === 0x90 && vel === 0)) held.current.delete(note);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => {
            const notes = [...held.current].sort((a, b) => a - b);
            if (notes.length < 3) return;
            const hit = identifyChord(notes, key, WALK_CHORDS);
            if (hit) addRef.current(hit.symbol, notes);
          }, 90);
        };
      });
      setMidiStatus('on');
    } catch {
      setMidiStatus('unavailable');
    }
  };

  const last = steps[steps.length - 1];
  return (
    <div>
      <header className="page-head">
        <span className="label">Play</span>
        <h1>Play chords on the gravity well</h1>
        <p>
          Tap a chord to play it. The closer to the centre, the closer to rest. The atlas voices each chord smoothly from the last, the way a keyboard player would, and
          tells you when your steps trace a pattern composers used.
        </p>
      </header>
      <PlayTabs current="walk" />
      <div className="walk-grid">
        <section className="panel walk-well">
          <div className="panel-head">
            <span className="label">{keyName(key)}</span>
            <label className="field">
              Key
              <select id="walk-key" value={keyToken} onChange={(e) => { setKeyToken(e.target.value); setSteps([]); }}>
                {WALK_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {keyName(parseKey(k))}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Well keyToken={keyToken} nodes={nodes} current={last?.base ?? null} trail={steps.slice(-4).map((s) => s.base)} depth={Math.max(depth, 2) as 2 | 3 | 4} onPick={(s) => add(s)} size={440} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            <div className="seg" role="group" aria-label="Bass">
              {(['root', 'smooth', 'first', 'second'] as Inversion[]).map((v) => (
                <button key={v} aria-pressed={inversion === v} onClick={() => setInversion(v)}>
                  {v === 'smooth' ? 'Smoothest bass' : v === 'root' ? 'Root in the bass' : v === 'first' ? '3rd in the bass' : '5th in the bass'}
                </button>
              ))}
            </div>
            <div className="seg" role="group" aria-label="Texture">
              {(['block', 'prelude', 'alberti'] as Texture[]).map((v) => (
                <button key={v} aria-pressed={texture === v} onClick={() => setTexture(v)}>
                  {v === 'block' ? 'Block chords' : v === 'prelude' ? 'Like the Prelude in C' : 'Broken (Alberti)'}
                </button>
              ))}
            </div>
          </div>
        </section>
        <section className="panel">
          <div className="panel-head">
            <span className="label">Your walk</span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button className="btn" onClick={replay} disabled={!steps.length}>
                Play it back
              </button>
              <button className="btn quiet" onClick={() => setSteps([])} disabled={!steps.length}>
                Clear
              </button>
            </span>
          </div>
          <div className="journey-list" aria-live="polite">
            {steps.length ? steps.map((s, i) => <span key={i}>{formatRoman(s.symbol)}</span>) : <span className="muted" style={{ border: 'none', background: 'none', fontFamily: 'var(--ui)', fontSize: '0.9rem' }}>Start at the centre: tap I, home.</span>}
          </div>
          {steps.length > 0 && <WalkBreath steps={steps} />}
          {last && depth >= 3 && (
            <p className="muted mono" style={{ fontSize: '0.8rem' }}>
              {formatRoman(last.symbol)}: {last.midi.map((m) => pitchName(spellMidi(m, last.chord.members), true)).join(' ')}
            </p>
          )}
          <div className="recognized" aria-live="polite">
            {found.map((m) => {
              const r = RECOGNIZERS.find((x) => x.id === m.id);
              const label = m.id === 'fifths-sequence' ? 'Falling fifths: dominoes' : r?.label ?? m.id;
              const pathId = m.id === 'fifths-sequence' ? 'fifths-sequence' : r?.path;
              return (
                <div className="hit" key={`${m.id}-${m.start}`}>
                  <span>
                    You walked: <b>{label}</b>
                  </span>
                  {pathId && pathById(pathId) && (
                    <a className="chip" href={`#path.${pathId}`}>
                      Hear the pattern →
                    </a>
                  )}
                </div>
              );
            })}
            {tonicized && (
              <div className="hit">
                <span>
                  You walked: <b>a brief visit to {keyName(lastTwo[0].chord.localKey)}</b>
                </span>
                <a className="chip" href="#path.tonicization">
                  Hear the pattern →
                </a>
              </div>
            )}
          </div>
          <div style={{ marginTop: 18, borderTop: '1px solid var(--rule)', paddingTop: 12 }}>
            <p className="muted" style={{ fontSize: '0.88rem' }}>
              Have a MIDI keyboard? Hold a chord and it lands on the terrain.
            </p>
            <button className="btn" onClick={connectMidi} disabled={midiStatus === 'on'}>
              {midiStatus === 'on' ? 'MIDI connected' : 'Connect MIDI keyboard'}
            </button>
            {midiStatus === 'unavailable' && <p className="muted" style={{ fontSize: '0.84rem', marginTop: 8 }}>MIDI is not available in this browser or window. It works in Chrome or Edge when the site is opened directly.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}

function WalkBreath({ steps }: { steps: Step[] }) {
  const w = 480;
  const h = 70;
  const x = (i: number) => 10 + (steps.length === 1 ? (w - 20) / 2 : (i * (w - 20)) / (steps.length - 1));
  const y = (v: number) => 6 + (1 - v) * (h - 12);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label="Tension of your walk, chord by chord">
      <line x1={0} x2={w} y1={y(0)} y2={y(0)} className="s-grid" />
      <polyline points={steps.map((s, i) => `${x(i)},${y(s.tension)}`).join(' ')} fill="none" className="s-tension" strokeWidth={2.2} strokeLinejoin="round" />
      {steps.map((s, i) => (
        <circle key={i} cx={x(i)} cy={y(s.tension)} r={3.5} className={s.tension < 0.2 ? 'f-rest' : 'f-tension'} />
      ))}
    </svg>
  );
}

